import React, { useState, useEffect, useContext, useRef } from 'react';
import { AudioProvider, AudioContext } from './contexts/AudioContext';
import MainScreen from './components/MainScreen';
import Login from './components/Login';
import './styles.css';
import { syncData } from './utils/sync';
import axiosInstance from './index';
import NavigationPopover from './components/NavigationPopover';
import { restoreChapterFromHash } from './utils/chapterHash';
import { initTheme } from './utils/theme';

// Inner component to access the AudioContext
function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem('isAuthenticated') === 'true'
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const { setSessions, setChapterProgress, setCurrentChapter, setIsPlaying, updateSession } = useContext(AudioContext);
  const restoredHash = useRef(false);

  useEffect(() => {
    const init = async () => {
      // Only verify authentication if we're online
      if (navigator.onLine && isAuthenticated) {
        try {
          await axiosInstance.get('/api/validate');
        } catch (error) {
          // Only log out if we get an explicit auth error
          if (error.response?.status === 401) {
            setIsAuthenticated(false);
            localStorage.removeItem('isAuthenticated');
          }
        }
      }
      setIsLoading(false);
    };
    init();

    // Set up online/offline listeners
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Sync effect that runs when authenticated and online
  useEffect(() => {
    if (isAuthenticated && isOnline) {
      syncData(handleLogout, setSessions, setChapterProgress);
    }
  }, [isAuthenticated, isOnline, setSessions, setChapterProgress]);

  // Restore #bookId:chapter once after login. Do not depend on updateSession —
  // it is a new function every render and would re-select the chapter in a loop.
  useEffect(() => {
    if (!isAuthenticated) {
      restoredHash.current = false;
      return;
    }
    if (isLoading || restoredHash.current) return;
    restoredHash.current = true;
    restoreChapterFromHash(setCurrentChapter, setIsPlaying, updateSession);
  }, [isAuthenticated, isLoading, setCurrentChapter, setIsPlaying, updateSession]);

  const handleLogin = () => {
    setIsAuthenticated(true);
    localStorage.setItem('isAuthenticated', 'true');
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('isAuthenticated');
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {isAuthenticated ? (
        <MainScreen 
          onLogout={handleLogout} 
          isOnline={isOnline}
        />
      ) : (
        <Login onLogin={handleLogin} isOnline={isOnline} />
      )}
      <NavigationPopover onLogout={handleLogout} />
    </>
  );
}

// Main App component that provides context
function App() {
  // Initialize theme on app startup
  useEffect(() => {
    initTheme();
  }, []);

  return (
    <AudioProvider>
      <AppContent />
    </AudioProvider>
  );
}

export default App;